import { Component, inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { NLSTransaction } from 'src/app/model/NLSTransaction';
import { NLSTransactionService } from 'src/app/services/nls-transaction/nls-transaction.service';
import { DialogService } from 'src/app/services/dialog/dialog.service';
import { FileService } from 'src/app/services/file/file.service';

/** Timeout duration when loading transaction details */
const TIMEOUT_DURATION = 10000; // 10 seconds

@Component({
    selector: 'app-transaction-details',
    templateUrl: './transaction-details.component.html',
    styleUrl: './transaction-details.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TransactionDetailsComponent implements OnInit {
  /** Dependency injections */
  private location: Location = inject(Location);
  private route: ActivatedRoute = inject(ActivatedRoute);
  private nlsTransactionService: NLSTransactionService = inject(NLSTransactionService);
  private toastr: ToastrService = inject(ToastrService);
  private dialogService: DialogService = inject(DialogService);
  private fileService: FileService = inject(FileService);

  /** Transaction data */
  public nlsTransaction?: NLSTransaction;
  public invoiceFiles: File[] | null = null;

  /** Loading flag */
  public isLoading: boolean = false;

  /**
   * On init, load the transaction details from the query parameter
   */
  ngOnInit(): void {
    // Get the transaction ID from the route parameter
    const transactionIdParam = this.route.snapshot.paramMap.get('id');

    if (!transactionIdParam) {
      this.toastr.error('Transaction ID not provided');
      this.goBack();
      return;
    }

    // Parse the transaction ID to a number
    const transactionId = parseInt(transactionIdParam, 10);
    if (isNaN(transactionId)) {
      this.toastr.error('Invalid transaction ID format');
      this.goBack();
      return;
    }

    this.isLoading = true;

    // First, try to get the transaction from the already loaded collection
    this.nlsTransaction = this.nlsTransactionService.getById(transactionId);

    if (this.nlsTransaction) {
      this.isLoading = false;
      this.loadInvoiceFiles(this.nlsTransaction.request_file_ids);
      return;
    }

    // Subscribe to changes in case the data is being loaded
    const sub = this.nlsTransactionService.changes$.subscribe({
      // On new load, try to find the transaction again
      next: (transactions: NLSTransaction[]) => {
        this.nlsTransaction = transactions.find(t => t.transaction_id === transactionId);
        if (this.nlsTransaction) {
          // Successfully found, stop loading
          this.isLoading = false;
          this.loadInvoiceFiles(this.nlsTransaction.request_file_ids);
          sub.unsubscribe();
        }
      },
      // Handle errors in loading
      error: (err: any) => {
        console.log('[TRANSACTION-DETAILS]: Error loading transactions', err);
        this.toastr.error('Error loading transaction data');

        this.isLoading = false;
        this.goBack();
        sub.unsubscribe();
      }
    });

    // Set a timeout to handle cases where data never loads
    setTimeout(() => {
      if (this.isLoading) {
        console.warn('[TRANSACTION-DETAILS]: Timeout waiting for transaction data');
        this.toastr.error('Transaction could not be found');

        this.isLoading = false;
        this.goBack();
        sub.unsubscribe();
      }
    }, TIMEOUT_DURATION); // 10 second timeout
  }

  /**
   * Helper function to load invoice files
   * 
   * @param fileIds The list of file IDs to load
   */
  loadInvoiceFiles(fileIds: number[]): void {
    // If no transaction is loaded, do nothing
    if (!this.nlsTransaction) {
      console.warn("[TRANSACTION-DETAILS]: No transaction loaded, cannot load invoice files");
      return;
    }

    // If there are no files, set to empty and return
    if (!fileIds || fileIds.length === 0) {
      this.invoiceFiles = [];
      return;
    }

    // Batch download files
    this.fileService.batchDownloadFiles(fileIds).subscribe({
      next: (files: {id: number, file: File}[]) => {
        this.invoiceFiles = files.map(f => f.file);
      },
      error: (err) => {
        console.error("[TRANSACTION-DETAILS]: Error downloading invoice files", err);
        this.toastr.error('Error downloading invoice files');
      }
    });
  }

  /**
   * Helper function to navigate back to the previous page
   */
  goBack(): void {
    this.location.back();
  }

  /**
   * Handle click of the "Authorize Transaction" button. This opens the transaction authorization dialog
   */
  onTransactionAuthorizationClick(): void {
    // Only show if nls transaction is loaded
    if (!this.nlsTransaction) {
      this.toastr.error('Transaction data is not loaded');
      return;
    }

    this.dialogService.openTransactionAuthorizationDialog({
      transactionId: this.nlsTransaction.transaction_id,
    });
  }

  /**
   * Handle click of a file link to open the file
   * @param index The index of the file in the invoiceFiles array
   */
  onOpenFile(index: number): void {
    if (!this.invoiceFiles) {
      this.toastr.error('No invoice files available');
      return;
    }

    const file = this.invoiceFiles[index];
    this.fileService.openFile(file);
  }
}
